/**
 * Core banking deposit types, and how to recognise one however it arrives.
 *
 * The ids are the backend's `Enums.DepositType`, which takes its values from
 * `CoreBankingProductType` — so they start at 2, not 0. They are also what
 * `GET /misc/cba/products?producttype=` expects.
 *
 * The same values reach the admin in three different spellings: the id
 * from `/enum/investment_product_types`, the enum name from a created
 * product's `deposit_type`, and the display name in a dropdown. Four places in
 * the app had their own hardcoded `"FixedDeposit" ? 3 : "RecurringDeposit" ? 4
 * : undefined` ternary, and every one of them silently dropped savings.
 */
export const DepositType = {
  SavingsDeposit: 2,
  FixedDeposit: 3,
  RecurringDeposit: 4,
  /** The only type whose booked rate an admin may override. */
  DynamicDeposit: 5,
} as const;

export type TDepositTypeId = (typeof DepositType)[keyof typeof DepositType];

/**
 * Deposit types that carry their own rate bands.
 *
 * Savings only, confirmed with the API owner: Flexisave and Habit Save price
 * from this chart, while fixed and recurring deposits take their rates from
 * core banking. The backend rejects a chart sent with any other deposit type.
 */
export const CHART_ENABLED_DEPOSIT_TYPES: TDepositTypeId[] = [
  DepositType.SavingsDeposit,
];

/** Deposit types whose min/max investment come from the core banking product. */
export const AUTO_FILLED_LIMIT_DEPOSIT_TYPES: TDepositTypeId[] = [
  DepositType.FixedDeposit,
  DepositType.RecurringDeposit,
  DepositType.DynamicDeposit,
];

/**
 * Deposit types that cannot pay interest upfront. The backend refuses the
 * product with "Upfront interest payment type is disabled for
 * recurring/dynamic investment product".
 */
export const NO_UPFRONT_INTEREST_DEPOSIT_TYPES: TDepositTypeId[] = [
  DepositType.RecurringDeposit,
  DepositType.DynamicDeposit,
];

const BY_NAME: Record<string, TDepositTypeId> = {
  savingsdeposit: DepositType.SavingsDeposit,
  fixeddeposit: DepositType.FixedDeposit,
  recurringdeposit: DepositType.RecurringDeposit,
  dynamicdeposit: DepositType.DynamicDeposit,
};

/**
 * The id for a deposit type given an id, an enum name, or a display name.
 *
 * `undefined` when it cannot tell — the caller should not fetch rather than
 * fetch for the wrong type.
 */
export function toDepositTypeId(
  value: string | number | null | undefined,
): TDepositTypeId | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const asNumber = Number(value);

  if (Number.isFinite(asNumber) && asNumber !== 0) {
    return isDepositTypeId(asNumber) ? asNumber : undefined;
  }

  // "Savings Deposit", "savings_deposit" and "SavingsDeposit" are all the same
  // type wearing different clothes.
  const key = String(value)
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  return BY_NAME[key];
}

/** Whether a deposit type carries its own interest rate chart. */
export function hasInterestRateChart(
  value: string | number | null | undefined,
): boolean {
  const id = toDepositTypeId(value);

  return id !== undefined && CHART_ENABLED_DEPOSIT_TYPES.includes(id);
}

/** Whether min/max investment come from core banking rather than the admin. */
export function hasCoreBankingLimits(
  value: string | number | null | undefined,
): boolean {
  const id = toDepositTypeId(value);

  return id !== undefined && AUTO_FILLED_LIMIT_DEPOSIT_TYPES.includes(id);
}

/**
 * Whether the booked interest rate may be typed by the admin.
 *
 * Only a dynamic deposit honours `override_chart_interest`; for any other type
 * the backend ignores the typed rate and books the chart's.
 */
export function isDynamicDeposit(
  value: string | number | null | undefined,
): boolean {
  return toDepositTypeId(value) === DepositType.DynamicDeposit;
}

/** Whether the deposit type refuses upfront interest payment. */
export function refusesUpfrontInterest(
  value: string | number | null | undefined,
): boolean {
  const id = toDepositTypeId(value);

  return id !== undefined && NO_UPFRONT_INTEREST_DEPOSIT_TYPES.includes(id);
}

function isDepositTypeId(value: number): value is TDepositTypeId {
  return (Object.values(DepositType) as number[]).includes(value);
}
